// src/components/common/Header.tsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Home, Plus, Grid3X3, ExternalLink } from 'lucide-react';

export const Header: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isPortfolioPage = location.pathname.startsWith('/portfolio/');

  if (isPortfolioPage) {
    return null; // Don't show header on individual portfolio pages
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handlePortfolioClick = () => {
    window.open('https://pushkarshinde.in', '_blank');
    closeMenu();
  };

  const navLinks = [
    { path: '/', label: 'Home', icon: Home, type: 'link' },
    { path: '/create', label: 'Create Portfolio', icon: Plus, type: 'link' },
    { path: '/portfolios', label: 'All Portfolios', icon: Grid3X3, type: 'link' },
    { path: '#', label: 'Developer', icon: ExternalLink, type: 'external', onClick: handlePortfolioClick },
  ];

  return (
    <header className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link 
            to="/" 
            className="text-2xl sm:text-3xl font-bold text-gray-800 hover:text-blue-600 transition-colors duration-200"
            onClick={closeMenu}
          >
            Evo<span className="text-blue-600">Folio</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navLinks.map(({ path, label, icon: Icon, type, onClick }) => (
              type === 'external' ? (
                <button
                  key={label}
                  onClick={onClick}
                  className="font-medium transition-all duration-200 flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-50 text-gray-600 hover:text-blue-600"
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </button>
              ) : (
                <Link
                  key={path}
                  to={path}
                  className={`font-medium transition-all duration-200 flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-50 ${
                    location.pathname === path 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </Link>
              )
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-50 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 bg-white">
            <nav className="flex flex-col space-y-2">
              {navLinks.map(({ path, label, icon: Icon, type, onClick }) => (
                type === 'external' ? (
                  <button
                    key={label}
                    onClick={onClick}
                    className="font-medium transition-all duration-200 flex items-center space-x-3 px-4 py-3 rounded-md mx-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 text-left"
                  >
                    <Icon size={20} />
                    <span>{label}</span>
                  </button>
                ) : (
                  <Link
                    key={path}
                    to={path}
                    onClick={closeMenu}
                    className={`font-medium transition-all duration-200 flex items-center space-x-3 px-4 py-3 rounded-md mx-2 ${
                      location.pathname === path 
                        ? 'text-blue-600 bg-blue-50' 
                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{label}</span>
                  </Link>
                )
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};